import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import heroSlide1 from '@/assets/hero-slide-1.jpg';
import heroSlide2 from '@/assets/hero-slide-2.jpg';
import heroSlide3 from '@/assets/hero-slide-3.jpg';

const slides = [heroSlide1, heroSlide2, heroSlide3];
const heroImage = '/og-image.png';

const HeroSection = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center">
      {/* Slideshow background, full bleed behind navbar */}
      {slides.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
        />
      ))}

      {/* Frosted glass overlay */}
      <div className="absolute inset-0 bg-background/50 backdrop-blur-sm" />

      {/* Subtle grid */}
      <div className="grid-bg pointer-events-none absolute inset-0 opacity-20" />

      {/* Content */}
      <div className="relative mx-auto max-w-4xl px-6 text-center pt-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/80 backdrop-blur-sm px-3.5 py-1.5 text-xs text-muted-foreground">
            <Sparkles size={12} className="text-primary" />
            Agentic AI Platform
          </div>

          <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            <span className="text-gradient">Autonomous AI agents</span> that deliver.
          </h1>

          <p className="mx-auto mt-3 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Deploy intelligent agents that plan, execute, and ship real work — with human oversight,
            governance controls, and full audit trails built in.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/login"
              className="group inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
            >
              Deploy your first agent
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative mx-auto mt-12 max-w-5xl px-6"
        >
          <div className="overflow-hidden rounded-2xl border border-border/40 shadow-2xl shadow-primary/10">
            <img
              src={heroImage}
              alt="Tasking.tech Agentic AI Platform"
              className="w-full h-auto"
              loading="eager"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
